import React, { useState } from 'react';
import {
  FaBaby,
  FaCameraRetro,
  FaCheck,
  FaChevronDown,
  FaEnvelopeOpenText,
  FaFeatherAlt,
  FaGift,
  FaGraduationCap,
  FaHeart,
  FaPaw,
  FaUsers,
} from 'react-icons/fa';
import { PRESET_OPTIONS } from '../../services/giftBuilderConfig';
import { DEFAULT_TEMPLATES } from '../../services/templates';
import { THEMES } from '../../themeConfig';

const TEMPLATE_CARD_META = {
  mom: {
    description: 'A warm letter-first gift for appreciation, family memories, and voice notes.',
    preview: 'letter',
    Icon: FaEnvelopeOpenText,
  },
  partner: {
    description: 'A romantic private story with film-like moments, letters, and voice.',
    preview: 'envelope',
    Icon: FaHeart,
  },
  wedding: {
    description: 'An elegant wedding favor with a welcome note, blessing wall, and keepsake card.',
    preview: 'ribbon',
    Icon: FaGift,
  },
  bestie: {
    description: 'A playful friendship archive for photos, inside jokes, and shared places.',
    preview: 'polaroid',
    Icon: FaCameraRetro,
  },
  birthday: {
    description: 'A bright celebration flow with wishes, photos, voice, and birthday love.',
    preview: 'gift',
    Icon: FaGift,
  },
  graduation: {
    description: 'A polished milestone story for proud moments, maps, and next chapters.',
    preview: 'map',
    Icon: FaGraduationCap,
  },
  remembrance: {
    description: 'A gentle memory archive for photos, voices, timelines, and family messages.',
    preview: 'archive',
    Icon: FaFeatherAlt,
  },
  pet_memorial: {
    description: 'A soft memorial keepsake for small habits, photos, and remembered details.',
    preview: 'archive',
    Icon: FaPaw,
  },
  new_baby: {
    description: 'A tender baby album for milestones, family wishes, and growth memories.',
    preview: 'album',
    Icon: FaBaby,
  },
  thank_you: {
    description: 'A clean gratitude note for appreciation, kindness, and one saveable card.',
    preview: 'note',
    Icon: FaUsers,
  },
};

const getTemplateTheme = (templateKey) => {
  const themeId = DEFAULT_TEMPLATES[templateKey]?.data?.themeId;
  return THEMES[themeId] || null;
};

const getTemplateSwatches = (templateTheme) => {
  if (!templateTheme) return [];
  if (templateTheme.id === 'wedding_favor') {
    return [templateTheme.cardBg, templateTheme.gold, templateTheme.sage].filter(Boolean);
  }
  if (templateTheme.id === 'friends_graduation') {
    return [templateTheme.cream, templateTheme.terracotta, templateTheme.denim].filter(Boolean);
  }
  return [
    templateTheme.cardBg,
    templateTheme.accentMuted || templateTheme.accentSoft,
    templateTheme.accent,
  ].filter(Boolean);
};

const TemplatePreview = ({ variant, Icon, swatches }) => (
  <span className="lumirae-theme-card-preview" data-preview={variant}>
    <span className="lumirae-theme-card-preview-shape">
      <Icon />
    </span>
    <span className="lumirae-theme-card-preview-mark" style={{ backgroundColor: swatches[1] || swatches[0] }} />
  </span>
);

const TemplateStep = ({ pageData, theme, onPresetChange }) => {
  const [isOpen, setIsOpen] = useState(!pageData.templateKey);
  const selectedOption = PRESET_OPTIONS.find((option) => option.key === pageData.templateKey);
  const selectedTheme = selectedOption ? getTemplateTheme(selectedOption.key) : null;
  const selectedMeta = TEMPLATE_CARD_META[selectedOption?.key] || {};
  const selectedSwatches = getTemplateSwatches(selectedTheme);
  const SelectedIcon = selectedMeta.Icon || FaGift;

  const choosePreset = (key) => {
    onPresetChange?.({ target: { value: key } });
    setIsOpen(false);
  };

  return (
    <section id="gift-builder-template" className="scroll-mt-6 space-y-5">
      <div className="lumirae-editor-panel p-5">
        <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
          Step 2 / Gift Theme
        </div>
        <div className="mt-3 rounded-[1.5rem] border bg-white/60 p-4" style={{ borderColor: 'var(--lumirae-border-default)' }}>
          <div className="flex items-center gap-3">
            <TemplatePreview variant={selectedMeta.preview || 'gift'} Icon={SelectedIcon} swatches={selectedSwatches} />
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold tracking-tight" style={{ color: 'var(--lumirae-text-primary)' }}>
                {selectedOption ? selectedOption.label : 'Choose the gift style'}
              </h3>
              <p className="mt-1 line-clamp-2 text-xs font-medium leading-relaxed" style={{ color: 'var(--lumirae-text-secondary)' }}>
                {selectedMeta.description || 'Pick the theme first. Colors, opening details, and recommended modules follow that choice.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen((value) => !value)}
            className="lumirae-ds-button lumirae-ds-button-secondary mt-4 flex w-full items-center justify-center gap-2 px-4 py-3"
          >
            {isOpen ? 'Hide Gift Types' : selectedOption ? 'Change Gift Type' : 'Choose Gift Type'}
            <FaChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {isOpen && (
      <div className="lumirae-theme-picker-grid">
        {PRESET_OPTIONS.map((option) => {
        const selected = pageData.templateKey === option.key;
        const templateTheme = getTemplateTheme(option.key);
        const meta = TEMPLATE_CARD_META[option.key] || {};
        const swatches = getTemplateSwatches(templateTheme);
        const Icon = meta.Icon || FaGift;
        const moduleCount = DEFAULT_TEMPLATES[option.key]?.modules?.length || 0;

        return (
          <button
            key={option.key}
            type="button"
            onClick={() => choosePreset(option.key)}
            className={`lumirae-theme-card ${selected ? 'is-selected' : ''}`}
            style={{
              '--theme-card-accent': templateTheme?.accent || theme.accent,
              '--theme-card-soft': templateTheme?.accentSoft || theme.accentSoft,
            }}
            aria-pressed={selected}
          >
            <span className="lumirae-theme-card-check" aria-hidden="true">
              <FaCheck />
            </span>

            <span className="lumirae-theme-card-top">
              <TemplatePreview variant={meta.preview || 'gift'} Icon={Icon} swatches={swatches} />
              <span className="lumirae-theme-card-swatches" aria-label={`${option.label} colors`}>
                {swatches.slice(0, 3).map((color, index) => (
                  <span key={`${option.key}_${index}`} style={{ backgroundColor: color }} />
                ))}
              </span>
            </span>

            <span className="lumirae-theme-card-copy">
              <strong>{option.label}</strong>
              <small>{meta.description || 'A polished Lumirae gift theme with matching modules and opening style.'}</small>
            </span>

            <span className="lumirae-theme-card-footer">
              <span>{moduleCount} modules</span>
              <em>{selected ? 'Selected' : 'Choose'}</em>
            </span>
          </button>
        );
        })}
      </div>
      )}

    <select
      value={pageData.templateKey || ''}
      onChange={onPresetChange}
      className="sr-only"
      aria-label="Gift theme"
      tabIndex={-1}
    >
      <option value="" disabled>Pick a gift vibe</option>
      {PRESET_OPTIONS.map((option) => (
        <option key={option.key} value={option.key}>{option.label}</option>
      ))}
    </select>
  </section>
  );
};

export default TemplateStep;
